import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Grid,
  Autocomplete,
  Divider,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@mui/x-date-pickers";
import { EditIcon, ShareIcon } from "../comps/CustomIcons";
import { getCancelPaymentApi, exportCancelPaymentApi } from "../services/reports.service";
import { getUserApi } from "../services/user.service";
import { useGetApi } from "../hooks/useGetApi";
import { toast } from "react-toastify";

const iconStyleProps = {
  backgroundColor: "rgba(240, 247, 252, 1)",
  width: "44px",
  height: "44px",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  borderRadius: "8px",
  cursor: "pointer",
  transition: "background-color 0.3s ease",
};

const menuItemStyle = {
  fontSize: "16px",
  fontWeight: "500",
  padding: "12px 16px",
};

const menuItems = [
  "Excel",
  "PDF",
  "Report Mail Send Scheduler",
  "Report Scheduler",
];

const paymentFilterList = [
  { label: "Advanced Patient Payment", value: "advancedPatientPayment" },
  { label: "Fund to patient", value: "fundToPatient" },
  { label: "Refund to patient", value: "refundToPatient" },
  { label: "Payment by relief", value: "paymentByRelief" },
  { label: "Other Payments", value: "otherPayments" },
];

const headLabel = [
  { id: 1, label: "Serial Number", value: "serialNumber" },
  { id: 2, label: "Patient ID", value: "patientId" },
  { id: 3, label: "Patient Name", value: "patientName" },
  { id: 4, label: "Amount", value: "amount" },
  { id: 5, label: "Receipt Number", value: "receiptNumber" },
  { id: 6, label: "Cancel Date", value: "cancelDate" },
  { id: 7, label: "Cancelled By", value: "cancelledBy" },
];

export default function ReportsCancelPayment() {
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    searchTemplate: null,
    cancelDate: null,
    paymentType: null,
    user: null,
  });

  // Report data state
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Get users for dropdown
  const {
    dataList: users,
    isLoading: usersLoading,
  } = useGetApi({
    apiFunction: getUserApi,
    params: { limit: 1000 },
  });

  const userOptions = (users || []).map(user => ({
    label: `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown User',
    value: user._id
  }));

  const [isExporting, setIsExporting] = useState(false);

  // Download menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    serialNumber: true,
    patientId: true,
    patientName: true,
    amount: true,
    receiptNumber: true,
    cancelDate: true,
    cancelledBy: true,
  });
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const filterMenuOpen = Boolean(filterAnchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilterClick = (event) => {
    setFilterAnchorEl(event.currentTarget);
  };
  const handleFilterClose = () => {
    setFilterAnchorEl(null);
  };

  const toggleColumn = (columnKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: "Cancel Payment Report",
        url: window.location.href,
      }).catch(() => {});
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.info("Link copied to clipboard");
    }
  };

  const handleMenuClick = (item) => {
    handleClose();
    if (item === "Excel") {
      handleExport();
    } else if (item === "PDF") {
      toast.info("PDF export feature coming soon");
    } else {
      toast.info(`${item} feature coming soon`);
    }
  };

  const handleInputChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...formData,
        cancelDate: formData.cancelDate ? formData.cancelDate.toISOString() : undefined,
        searchTemplate: formData.searchTemplate?.value || undefined,
        paymentType: formData.paymentType?.value || undefined,
        user: formData.user?.value || undefined,
      };

      // Remove undefined values
      Object.keys(params).forEach(key => {
        if (params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await getCancelPaymentApi(params);
      
      if (response?.data) {
        setReportData(response.data.payments || response.data.records || []);
        setTotalCount(response.data.pagination?.totalRecords || response.data.total || 0);
        toast.success('Report generated successfully');
      } else {
        setError('No data found for the selected criteria');
      }
    } catch (err) {
      setError(err.message || 'Failed to generate report');
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      // Get current filter parameters
      const exportParams = {
        cancelDate: formData.cancelDate ? formData.cancelDate.toISOString() : undefined,
        user: formData.user?.value,
        page: 1,
        limit: 250,
      };

      Object.keys(exportParams).forEach((k) => {
        if (exportParams[k] === undefined || exportParams[k] === "") delete exportParams[k];
      });

      const result = await exportCancelPaymentApi(exportParams);
      
      if (result.success) {
        toast.success(`Successfully exported ${result.count} records to Excel!`);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Export failed: ${error.message || 'Unknown error'}`);
    } finally {
      setIsExporting(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Box sx={{ my: "20px" }}>
            <Button
              startIcon={<ArrowLeft />}
              onClick={handleBack}
              sx={{
                color: "#5D6A82",
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "bold",
                padding: 0,
                transition: "none !important",
                marginBottom: "16px",
              }}
            >
              Back
            </Button>
            <Typography sx={{
              fontFamily: "Open Sans",
              fontWeight: 600,
              fontSize: "20px",
              lineHeight: "29px",
              letterSpacing: "0px",
              color: "#5D6A82",
              marginBottom: "1rem",
            }}>
              Cancel Payment Report
            </Typography>
          </Box>

          {/* Search Form */}
          <Box>
            <Grid spacing={2} container>
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={paymentFilterList || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.searchTemplate}
                  onChange={(event, newValue) => handleInputChange('searchTemplate', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Template"
                      name="searchTemplate"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  name="cancelDate"
                  label="Cancel Date"
                  value={formData.cancelDate}
                  onChange={(newValue) => handleInputChange('cancelDate', newValue)}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={paymentFilterList || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.paymentType}
                  onChange={(event, newValue) => handleInputChange('paymentType', newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Payment Type"
                      name="paymentType"
                      fullWidth
                    />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={userOptions || []}
                  getOptionLabel={(option) => option?.label || ""}
                  value={formData.user}
                  onChange={(event, newValue) => handleInputChange('user', newValue)}
                  loading={usersLoading}
                  renderInput={(params) => (
                    <TextField {...params} label="User" name="user" fullWidth />
                  )}
                  sx={{ bgcolor: "white" }}
                />
              </Grid>
            </Grid>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: "20px",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="secondary"
                onClick={handleExport}
                disabled={isExporting}
                sx={{ minWidth: 120 }}
              >
                {isExporting ? <CircularProgress size={20} /> : 'Export'}
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                sx={{
                  textTransform: "none",
                  backgroundColor: "rgba(1, 121, 176, 1)",
                  width: "150px",
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Search'}
              </Button>
            </Box>
          </Box>

          {/* Error Message */}
          {error && (
            <Alert severity="error" sx={{ my: 2 }}>
              {error}
            </Alert>
          )}

          {/* Results Table */}
          <Box
            sx={{
              border: "1px solid rgba(223, 232, 239, 1)",
              borderRadius: "8px",
              my: "20px",
              p: "20px",
            }}
          >
            {/* dynamic button */}
            <Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 1,
                  pb: "15px",
                }}
              >
                <Typography
                  sx={{ fontSize: "14px", color: "rgba(93, 106, 130, 1)" }}
                >
                  Rows: {reportData?.length || 0} | Columns: {Object.values(visibleColumns).filter(v => v).length}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box sx={iconStyleProps} onClick={handleFilterClick}>
                    <EditIcon
                      sx={{
                        cursor: "pointer",
                        color: "transparent",
                        fontSize: "18px",
                      }}
                    />
                  </Box>
                  <Box sx={iconStyleProps} onClick={handleShare}>
                    <ShareIcon />
                  </Box>
                  <Box sx={iconStyleProps} onClick={handleClick}>
                    <IconButton>
                      <FileDownloadOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Table */}
            <TableContainer component={Paper} elevation={0}>
              <Table aria-label="cancel payment report table" sx={{ minWidth: 800 }}>
                <TableHead
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": { fontWeight: "bold" },
                  }}
                >
                  <TableRow>
                    {visibleColumns.serialNumber && <TableCell>Serial Number</TableCell>}
                    {visibleColumns.patientId && <TableCell>Patient ID</TableCell>}
                    {visibleColumns.patientName && <TableCell>Patient Name</TableCell>}
                    {visibleColumns.amount && <TableCell>Amount</TableCell>}
                    {visibleColumns.receiptNumber && <TableCell>Receipt Number</TableCell>}
                    {visibleColumns.cancelDate && <TableCell>Cancel Date</TableCell>}
                    {visibleColumns.cancelledBy && <TableCell>Cancelled By</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2 }}>Loading report data...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : !Array.isArray(reportData) || reportData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={Object.values(visibleColumns).filter(v => v).length} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="textSecondary">No data available. Please search with filters.</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    reportData.map((row, index) => (
                      <TableRow key={row?._id || index}>
                        {visibleColumns.serialNumber && <TableCell>{row?.serialNumber || index + 1}</TableCell>}
                        {visibleColumns.patientId && <TableCell>{row?.patientId || 'N/A'}</TableCell>}
                        {visibleColumns.patientName && <TableCell>{row?.patientName || 'N/A'}</TableCell>}
                        {visibleColumns.amount && <TableCell>{row?.amount ? `₹${row.amount.toLocaleString()}` : 'N/A'}</TableCell>}
                        {visibleColumns.receiptNumber && <TableCell>{row?.receiptNumber || 'N/A'}</TableCell>}
                        {visibleColumns.cancelDate && <TableCell>{row?.cancelDate || 'N/A'}</TableCell>}
                        {visibleColumns.cancelledBy && <TableCell>{row?.cancelledBy || 'N/A'}</TableCell>}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {reportData?.length > 0 && (
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Box>

          {/* Download Menu */}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            slotProps={{
              sx: {
                borderRadius: "12px",
                boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                padding: "8px 0",
                width: "250px",
              },
            }}
          >
            {menuItems.map((item, index) => (
              <React.Fragment key={item}>
                <MenuItem onClick={() => handleMenuClick(item)} sx={menuItemStyle}>
                  {item}
                </MenuItem>
                {index !== menuItems.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </Menu>

          {/* Filter Menu for Column Visibility */}
          <Menu
            anchorEl={filterAnchorEl}
            open={filterMenuOpen}
            onClose={handleFilterClose}
            slotProps={{
              sx: {
                borderRadius: "12px",
                boxShadow: "0px 2px 10px rgba(0,0,0,0.1)",
                padding: "8px 0",
                width: "200px",
              },
            }}
          >
            <MenuItem sx={{ ...menuItemStyle, fontWeight: "bold" }} disabled>
              Show/Hide Columns
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => toggleColumn('serialNumber')} sx={menuItemStyle}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ flex: 1 }}>Serial Number</Box>
                {visibleColumns.serialNumber && <Box sx={{ color: 'primary.main' }}>✓</Box>}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => toggleColumn('patientId')} sx={menuItemStyle}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ flex: 1 }}>Patient ID</Box>
                {visibleColumns.patientId && <Box sx={{ color: 'primary.main' }}>✓</Box>}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => toggleColumn('patientName')} sx={menuItemStyle}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ flex: 1 }}>Patient Name</Box>
                {visibleColumns.patientName && <Box sx={{ color: 'primary.main' }}>✓</Box>}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => toggleColumn('amount')} sx={menuItemStyle}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ flex: 1 }}>Amount</Box>
                {visibleColumns.amount && <Box sx={{ color: 'primary.main' }}>✓</Box>}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => toggleColumn('receiptNumber')} sx={menuItemStyle}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ flex: 1 }}>Receipt Number</Box>
                {visibleColumns.receiptNumber && <Box sx={{ color: 'primary.main' }}>✓</Box>}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => toggleColumn('cancelDate')} sx={menuItemStyle}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ flex: 1 }}>Cancel Date</Box>
                {visibleColumns.cancelDate && <Box sx={{ color: 'primary.main' }}>✓</Box>}
              </Box>
            </MenuItem>
            <MenuItem onClick={() => toggleColumn('cancelledBy')} sx={menuItemStyle}>
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Box sx={{ flex: 1 }}>Cancelled By</Box>
                {visibleColumns.cancelledBy && <Box sx={{ color: 'primary.main' }}>✓</Box>}
              </Box>
            </MenuItem>
          </Menu>
        </Box>
      </Box>
    </Container>
  );
}
